<template>
  <q-card
    flat
    bordered
    class="q-pa-none full-width"
    :class="{
      'bg-grey-1': showSettings && !selected && !isTargetOrParentHasInvalidWorkflow,
      'bg-grey-2': selected && !isTargetOrParentHasInvalidWorkflow,
      'bg-red-1': isTargetOrParentHasInvalidWorkflow,
    }"
  >
    <q-tooltip v-if="isTargetOrParentHasInvalidWorkflow">
      {{ invalidWorkflowTooltip }}
    </q-tooltip>
    <q-card-section
      class="q-ma-none q-py-xs row items-center q-col-gutter-md"
      :class="{
        'hover-detect-root': !hasParentTarget,
        'hover-detect': hasParentTarget,
      }"
    >
      <div class="row q-pr-sm col-xs-12 col-md-5 items-center">
        <div class="col-xs-12 col-md-auto row items-center q-mb-xs q-mb-md-none">
          <div
            v-if="editable"
            class="col-auto"
          >
            <q-icon
              name="drag_indicator"
              size="sm"
              color="grey-5"
              class="target-drag-handle"
              :class="{
                'show-on-hover-root': !hasParentTarget && $q.screen.gt.sm,
                'show-on-hover': hasParentTarget && $q.screen.gt.sm,
              }"
            />
          </div>
          <div class="col-auto column">
            <q-checkbox
              v-if="selectable"
              :model-value="selected"
              size="xs"
              class="col-auto"
              data-e2e="checkbox-target"
              @update:model-value="onSetSelected"
            />
          </div>
          <div class="col-auto row items-center relative-position">
            <q-icon
              name="warning"
              color="warning"
              size="xs"
              class="q-mr-xs"
              :class="{'invisible' : !isTargetStatusInvalid}"
            >
              <q-tooltip>
                {{ $t('programs.targetCard.tooltipInvalidStatus') }}
              </q-tooltip>
            </q-icon>
            <div class="q-ml-sm q-mr-lg">
              <q-btn-dropdown
                :icon="getTargetProperty(target.status, 'icon', null)"
                :color="isTargetStatusInvalid ? 'white' : getTargetProperty(target.status, 'color', 'primary')"
                :style="{
                  backgroundColor: isTargetStatusInvalid
                    ? 'white'
                    : getTargetProperty(target.status, 'color', 'primary'),
                }"
                :text-color="
                  isTargetStatusInvalid ? 'black' : getTextColor(getTargetProperty(target.status, 'color', 'primary'))
                "
                :disable="disable || offlineMode"
                size="sm"
                rounded
                unelevated
                dense
                class="q-px-sm target-status-chip"
                data-e2e="dropdown-target-status"
              >
                <q-list>
                  <q-item
                    v-for="targetStatus in availableTargetWorkflowStatuses"
                    :key="targetStatus.value"
                    v-close-popup
                    clickable
                    :active="targetStatus.value === target.status"
                    @click="onChangeTargetStatus(targetStatus.value)"
                  >
                    <q-item-section avatar>
                      <q-icon
                        :name="targetStatus.icon"
                        :color="targetStatus.color"
                        :style="{ color: targetStatus.color }"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ targetStatus.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
              <q-tooltip
                anchor="bottom middle"
                self="top middle"
              >
                {{ $t('programs.targetCard.tooltipStatusLabel') }}:
                {{
                  getTargetProperty(
                    target.status,
                    'label',
                    $t('programs.targetCard.invalidStatusDropdownLabel')
                  )
                }}
              </q-tooltip>
            </div>
          </div>
        </div>

        <div class="col-xs-12 col-md row items-center q-pl-none q-pl-md-md">
          <div class="row col items-start">
            <q-select
              v-if="editable && showTargetDiscriminativeStimulus"
              :model-value="target.discriminativeStimulus"
              :placeholder="$t('programs.targetCard.inputDiscriminativeStimulusLabel')"
              use-input
              hide-selected
              borderless
              fill-input
              input-debounce="0"
              :options="filteredDiscriminativeStimulusList"
              input-class="text-grey-7"
              dense
              hide-dropdown-icon
              class="col-12 ellipsis target-sd-select"
              @filter="onFilterDiscriminativeStimulusList"
              @input-value="onSetDiscriminativeStimulusListValue"
            >
              <template #prepend>
                <div>
                  <q-chip
                    :label="$t('programs.targetCard.sDLabel')"
                    color="grey-3"
                    text-color="grey-7"
                    size="sm"
                    dense
                    class="q-ma-none q-py-sm q-px-sm text-weight-medium text-caption"
                  />
                </div>
              </template>
            </q-select>
            <q-input
              v-if="editable"
              ref="targetLabel"
              v-model="targetLabel"
              :placeholder="$t('programs.targetCard.inputTargetPlaceholder')"
              :hint="targetLabelHint"
              hide-hint
              dense
              borderless
              hide-bottom-space
              data-e2e="input-title-target"
              :rules="[val => (
                !!val
                || (target.editStatus === 'create') && !targetTypeHasSubTargets)
                || $t('programs.targetCard.inputTargetLabelValidator')]
              "
              lazy-rules
              class="col-12"
              :input-class="{ 'text-bold': showSettings, 'text-black': true }"
              @keydown.enter.prevent="onEnterPressed"
            >
              <template
                v-if="targetDuplicateWarning"
                #append
              >
                <q-icon
                  name="warning"
                  color="yellow"
                >
                  <q-tooltip>{{ $t('programs.targetCard.itemDuplicateWarning') }}</q-tooltip>
                </q-icon>
              </template>
              <q-icon
                v-if="isTargetInvalidDefaultWorkflow"
                name="warning"
                color="warning"
                size="md"
              >
                <q-tooltip>
                  {{ $t('programs.targetCard.tooltipParentTargetInvalidDefaultWorkflow') }}
                </q-tooltip>
              </q-icon>
            </q-input>
            <span v-else>
              <div
                v-if="!!target.discriminativeStimulus"
                class="text-grey-7 q-mb-xs"
              >
                {{ target.discriminativeStimulus }}
              </div>
              <div class="text-black">{{ target.label }}</div>
            </span>
            <div
              v-if="targetTypeHasSubTargets && $q.screen.gt.sm"
              class="col-12 row justify-start q-mt-xs"
            >
              <q-btn
                v-if="targetTypeHasSubTargets && !isIntervalSubTarget"
                flat
                dense
                no-caps
                color="grey-7"
                :label="targetTypeSettingsLabel"
                :icon-right="showSettings ? 'expand_less' : 'expand_more'"
                class="q-pa-none no-hover-bg text-caption subtarget-settings-btn"
                data-e2e="button-edit-steps"
                @click="onShowSettingsToggle"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-sm-5 col-md-3 text-body2 q-gutter-y-md q-pl-md q-pl-md-lg q-mb-md q-mb-md-none">
        <div
          v-if="showTargetStatus && getTargetProperty(target.status, 'hasIntroductionDate', true)"
          :class="{ 'cursor-pointer': !disable }"
        >
          <q-icon
            :name="getTargetProperty('probe', 'icon')"
            :color="getTargetProperty('probe', 'color')"
            :style="{ color: getTargetProperty('probe', 'color') }"
            size="xs"
          >
            <q-tooltip>
              {{ $t('programs.targetCard.tooltipTargetText') }}
              <span v-if="!disable">{{ $t('programs.targetCard.tooltipTargetEditSpanText') }}</span>
            </q-tooltip>
          </q-icon>
          {{ displayIntroductionDate }}
          <q-popup-proxy
            v-if="!disable"
            ref="qIntroductionDateProxy"
          >
            <!-- empty span fixes q-popup-proxy width -->
            <span />
            <q-date
              v-model="editIntroductionDate"
              :disable="offlineMode"
              mask="YYYY-MM-DD"
              no-unset
              @update:model-value="onSelectIntroductionDate"
            />
          </q-popup-proxy>
        </div>
        <div
          v-if="showTargetStatus && getTargetProperty(target.status, 'hasMasteryDate', true)"
          class="row items-center q-gutter-x-sm"
        >
          <div :class="{ 'cursor-pointer': !disable }">
            <q-icon
              :name="getTargetProperty('mastered', 'icon')"
              :color="getTargetProperty('mastered', 'color')"
              :style="{ color: getTargetProperty('mastered', 'color') }"
              size="xs"
            >
              <q-tooltip>
                {{ $t('programs.targetCard.tooltipTargetDateText') }}
                <span v-if="!disable">{{ $t('programs.targetCard.tooltipTargetEditSpanText') }}</span>
              </q-tooltip>
            </q-icon>
            {{ displayMasteryDate }}
            <q-popup-proxy
              v-if="!disable"
              ref="qMasteryDateProxy"
            >
              <span />
              <q-date
                v-model="editMasteryDate"
                :disable="offlineMode"
                mask="YYYY-MM-DD"
                no-unset
                @update:model-value="onSelectMasteryDate"
              />
            </q-popup-proxy>
          </div>
        </div>
        <div
          v-if="showTargetStatus && getTargetProperty(target.status, 'hasHoldDate', false)"
          :class="{ 'cursor-pointer': !disable }"
        >
          <q-icon
            :name="getTargetProperty('hold', 'icon')"
            :color="getTargetProperty('hold', 'color')"
            :style="{ color: getTargetProperty('hold', 'color') }"
            size="xs"
          >
            <q-tooltip>
              {{ $t('programs.targetCard.tooltipTargetDateText') }}
              <span v-if="!disable">{{ $t('programs.targetCard.tooltipTargetEditSpanText') }}</span>
            </q-tooltip>
          </q-icon>
          {{ displayHoldDate }}
          <q-popup-proxy
            v-if="!disable"
            ref="qHoldDateProxy"
          >
            <!-- empty span fixes q-popup-proxy width -->
            <span />
            <q-date
              v-model="editHoldDate"
              mask="YYYY-MM-DD"
              no-unset
              @update:model-value="onSelectHoldDate"
            />
          </q-popup-proxy>
        </div>
        <div
          v-if="showTargetStatus && getTargetProperty(target.status, 'hasAbortedDate', false)"
          :class="{ 'cursor-pointer': !disable }"
        >
          <q-icon
            :name="getTargetProperty('aborted', 'icon')"
            :color="getTargetProperty('aborted', 'color')"
            :style="{ color: getTargetProperty('aborted', 'color') }"
            size="xs"
          >
            <q-tooltip>
              {{ $t('programs.targetCard.tooltipTargetDateText') }}
              <span v-if="!disable">{{ $t('programs.targetCard.tooltipTargetEditSpanText') }}</span>
            </q-tooltip>
          </q-icon>
          {{ displayAbortedDate }}
          <q-popup-proxy
            v-if="!disable"
            ref="qAbortedDateProxy"
          >
            <!-- empty span fixes q-popup-proxy width -->
            <span />
            <q-date
              v-model="editAbortedDate"
              mask="YYYY-MM-DD"
              no-unset
              @update:model-value="onSelectAbortedDate"
            />
          </q-popup-proxy>
        </div>
      </div>
      <div
        class="col-xs-12 col-sm-7 col-md-4 q-py-sm q-pr-sm q-pl-md q-pl-md-lg
        row no-wrap items-center justify-between self-stretch self-md-center"
      >
        <div class="col column q-gutter-y-xs q-mr-sm items-start justify-center target-settings-column">
          <div
            v-if="hasDependantPromptLevel"
            class="row items-center no-wrap q-gutter-x-xs q-pr-sm q-py-xs"
          >
            <q-input
              v-model.number="targetSuccessfulTrials"
              type="number"
              :rules="targetTrialValidator"
              min="0"
              outlined
              dense
              class="col"
              hide-bottom-space
              input-class="text-center q-pa-none"
              :disable="disable || offlineMode"
            />
            <div class="text-grey-7 text-weight-bold q-px-xs">
              /
            </div>
            <q-input
              v-model.number="targetNumberOfTrials"
              type="number"
              :rules="targetTrialValidator"
              min="0"
              outlined
              dense
              class="col"
              hide-bottom-space
              input-class="text-center q-pa-none"
              :disable="disable || offlineMode"
            />
          </div>

          <template v-else>
            <div
              v-if="editable && hasAvailableTargetDataTypes"
              class="relative-position cursor-pointer"
            >
              <q-tooltip>
                {{ $t('programs.targetCard.tooltipTargetType') }}
              </q-tooltip>
              <q-btn-dropdown
                icon="sym_o_target"
                :label="targetTypeLabel"
                flat
                dense
                no-caps
                color="black"
                :disable="disable || offlineMode"
                class="text-body2 text-weight-regular no-hover-bg truncate-settings-btn"
                data-e2e="dropdown-target-type"
              >
                <q-list class="text-no-wrap">
                  <q-item
                    v-for="targetDataType in availableTargetDataTypes"
                    :key="targetDataType.value"
                    v-close-popup
                    clickable
                    :active="isTargetDataTypeActive(targetDataType.value)"
                    @click="showChangeTargetAttention(targetDataType.value)"
                  >
                    <q-item-section>
                      <q-item-label>{{ targetDataType.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </div>

            <div
              v-else-if="showTargetType"
              class="row items-center text-body2 text-grey-8 q-pr-sm q-py-xs"
            >
              <q-icon
                name="sym_o_target"
                size="xs"
                class="q-mr-sm"
              />
              {{ targetTypeLabel }}
            </div>
          </template>

          <div
            v-if="hasMaintenanceLevel"
            class="row items-center no-wrap relative-position cursor-pointer"
          >
            <q-tooltip>
              {{ $t('programs.targetCard.textTitleMaintenanceInterval') }}
            </q-tooltip>
            <q-btn-dropdown
              icon="schedule"
              :label="getMaintenanceLabel(target.maintenanceInterval)"
              :disable="disable || offlineMode"
              flat
              dense
              no-caps
              color="black"
              class="text-body2 text-weight-regular no-hover-bg truncate-settings-btn"
            >
              <div class="q-py-xs q-px-md text-center text-subtitle2 bg-primary text-white">
                {{ $t('programs.targetCard.textTitleMaintenanceInterval') }}
              </div>
              <q-separator />
              <q-list>
                <q-item
                  v-for="maintenanceInterval in maintenanceIntervals"
                  :key="maintenanceInterval.value"
                  v-close-popup
                  clickable
                  :active="maintenanceInterval.value === target.maintenanceInterval"
                  @click="onChangeMaintenanceInterval(maintenanceInterval.value)"
                >
                  <q-item-section>
                    <q-item-label>{{ maintenanceInterval.label }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
          <div
            v-if="hasPromptLevelFading"
            class="row items-center no-wrap relative-position cursor-pointer"
          >
            <q-tooltip>
              {{ $t('programs.targetCard.textTitlePromptLevel') }}
            </q-tooltip>
            <q-btn-dropdown
              icon="sym_o_prompt_suggestion"
              :label="
                getPromptLevelProperty(
                  target.currentPromptLevel,
                  'trialOutcome',
                  $t('programs.targetCard.defaultCurrentPromptLevelLabel')
                )
              "
              flat
              dense
              no-caps
              color="black"
              class="text-body2 text-weight-regular no-hover-bg truncate-settings-btn"
            >
              <div class="q-py-xs q-px-md text-center text-subtitle2 bg-primary text-white">
                {{ $t('programs.targetCard.textTitlePromptLevel') }}
              </div>
              <q-separator />
              <q-list>
                <q-item
                  v-for="promptLevelOption in activePromptLevelOptions"
                  :key="promptLevelOption.id"
                  v-close-popup
                  clickable
                  :style="getPromptLevelListItemStyle(promptLevelOption)"
                  class="prompt-level"
                  @click="onChangeCurrentPromptLevel(promptLevelOption.id)"
                >
                  <q-item-section>
                    <q-item-label>{{ promptLevelOption.trialOutcome }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
          <q-btn
            v-if="targetTypeHasSubTargets && !isIntervalSubTarget && !$q.screen.gt.sm"
            flat
            dense
            no-caps
            color="grey-7"
            :label="targetTypeSettingsLabel"
            :icon-right="showSettings ? 'expand_less' : 'expand_more'"
            class="q-mr-sm no-hover-bg text-caption q-ml-xs"
            data-e2e="button-edit-steps"
            @click="onShowSettingsToggle"
          />
        </div>

        <div class="col-auto row items-center no-wrap q-pr-sm self-end self-md-center responsive-actions-container">
          <q-btn
            v-if="canShowData && $q.screen.gt.md && selectable"
            round
            flat
            color="dark-variant"
            icon="bar_chart"
            class="q-mr-xs"
            :class="{
              'show-on-hover-root': !hasParentTarget,
              'show-on-hover': hasParentTarget,
            }"
            :disable="!target.id || offlineMode"
            @click="onShowHistory"
          />

          <q-btn
            v-if="canShowTargetSettings && $q.screen.gt.md && selectable"
            round
            flat
            color="dark-variant"
            icon="settings"
            class="q-mr-xs"
            :class="{
              'show-on-hover-root': !hasParentTarget,
              'show-on-hover': hasParentTarget,
            }"
            :disable="offlineMode"
            @click="onEditSettings"
          />

          <q-btn
            round
            flat
            color="dark-variant"
            icon="more_horiz"
            data-e2e="button-target-options"
            :class="{
              'show-on-hover-root': !hasParentTarget && $q.screen.gt.md,
              'show-on-hover': hasParentTarget && $q.screen.gt.md,
            }"
            :disable="offlineMode"
          >
            <q-menu>
              <q-list class="text-no-wrap">
                <q-item
                  v-if="canShowData && (!$q.screen.gt.md || !selectable)"
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="!target.id"
                  color="dark"
                  data-e2e="list-item-view-target-data"
                  @click="onShowHistory"
                >
                  <q-item-section avatar>
                    <q-icon
                      name="bar_chart"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('programs.targetCard.listItemTargetHistory') }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="canShowTargetSettings && (!$q.screen.gt.md || !selectable)"
                  v-ripple
                  v-close-popup
                  clickable
                  color="dark"
                  data-e2e="list-item-settings-target"
                  @click="onEditSettings"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="settings"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.targetCard.listItemTargetSettings') }}
                  </q-item-section>
                </q-item>
                <q-separator v-if="!$q.screen.gt.md" />
                <q-item
                  v-if="editable"
                  v-ripple
                  v-close-popup
                  clickable
                  color="dark"
                  data-e2e="list-item-duplicate-target"
                  @click="onDuplicate"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="copy_all"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('programs.targetCard.listItemDuplicate') }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="editable"
                  v-ripple
                  v-close-popup
                  clickable
                  color="dark"
                  data-e2e="list-item-delete-target"
                  @click="onDelete"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="delete"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('programs.targetCard.listItemDelete') }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  color="dark"
                  data-e2e="list-item-copy-target-info"
                  @click="onCopySupportInfo"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="content_copy"
                      color="dark"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('common.copySupportInfo') }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </div>
    </q-card-section>
    <template v-if="showSettings && (!targetTypeHasSubTargets || hasParentTarget)">
      <q-card-actions
        class="row items-center text-dark text-caption q-py-sm q-px-md cursor-pointer"
        @click="showSettings = !showSettings"
      >
        <q-space />
        <div class="col-auto">
          <q-btn
            color="dark-variant"
            :label="$t('programs.targetCard.buttonCloseSettings')"
            flat
            dense
            icon-right="expand_less"
            size="sm"
          />
        </div>
      </q-card-actions>
    </template>
    <q-slide-transition>
      <div
        v-if="showSettings"
      >
        <q-separator />
        <q-card-section
          v-if="targetTypeHasSubTargets"
        >
          <target-list
            v-if="targetTypeHasSubTargets && !isIntervalSubTarget"
            v-model="programTargets"
            :parent-target="targetId"
            :program="program"
            :show-target-status="showTargetStatus"
            :target-workflows="workflows"
            :program-workflow="programWorkflow"
            :no-bulk-actions="noBulkActions"
            :read-only="!editable"
            :disable="disable || offlineMode"
            :disable-add-target="!editable"
            :disable-discriminative-stimulus="isIntervalTarget"
            :package-prompt-level-templates="packagePromptLevelTemplates"
            @load-subtargets="$emit('load-subtargets', $event)"
            @update-target-progress-settings="$emit('update-target-progress-settings', $event)"
            @update-parent-target-status="$emit('update-parent-target-status', $event)"
          />
        </q-card-section>
        <q-card-section
          v-if="editable"
          class="row q-col-gutter-md"
        >
          <p class="col-12 text-overline text-dark-variant text-uppercase q-ma-none">
            {{ $t('programs.targetCard.textTitleSettings') }}
          </p>
          <template v-if="showTargetDurationSettings">
            <div
              v-if="hasMultiplePromptLevels"
              class="col-12"
            >
              <hr-segmented-btn
                v-model="targetDurationPromptLevelTiming"
                :options="durationPromptLevelTimingOptions"
                :disable="disable || offlineMode"
                spread
                hide-selected-check
                :aria-label="$t('editProgram.durationPromptLevelTimingLabel')"
                class="col-12 target-duration-toggle"
              />
            </div>
            <div class="col-xs-12 col-md-6">
              <hr-checkbox
                v-model="targetFillZeroDuration"
                :label="$t('editProgram.recordZeroDurationLabel')"
                :disable="disable || offlineMode"
              />
            </div>
          </template>
          <q-select
            v-if="targetTypeHasTimerType"
            v-model="targetTimerType"
            outlined
            :options="timerTypes"
            :label="$t('editProgram.selectTimerTypeLabel')"
            hide-hint
            hide-bottom-space
            emit-value
            map-options
            clearable
            lazy-rules
            class="col-12"
          />
          <q-input
            v-if="targetTypeHasTimerPreset"
            v-model="targetTimerPresetFormatted"
            :label="$t('editProgram.inputTimerPresetLabel')"
            outlined
            :rules="[val => stringToDuration(val) > 0
              || $t('editProgram.inputTimerPresetValidator')]"
            lazy-rules
            mask="#:##:##"
            fill-mask="0"
            hide-hint
            hide-bottom-space
            input-class="text-right"
            class="col-xs-12 col-md-6"
          />
          <template v-if="targetTypeHasIntervalSettings">
            <q-input
              v-model="targetIntervalFormatted"
              :label="$t('editProgram.inputIntervalLabel')"
              outlined
              mask="#:##:##"
              fill-mask="0"
              hide-hint
              hide-bottom-space
              input-class="text-right"
              :rules="[val => stringToDuration(val) > 0 || $t('editProgram.inputIntervalValidator')]"
              lazy-rules
              class="col-xs-12 col-md-6"
            />
            <div class="col-xs-12 col-md-6 flex items-center">
              <q-checkbox
                v-model="targetTimerAutoStart"
                :label="$t('editProgram.syncTimerWithSessionDurationLabel')"
              />
            </div>
            <div class="col-12 q-py-none">
              <q-checkbox
                v-model="targetTimerWarningValue"
                :label="$t('editProgram.timerWarningLabel')"
              />
            </div>
            <template v-if="targetTimerWarningValue">
              <div class="col-12 q-pt-none q-pl-md">
                <q-checkbox
                  v-model="targetTimerIntervalPauseValue"
                  :label="$t('editProgram.timerIntervalPauseLabel')"
                />
              </div>
              <q-input
                v-model.number="targetTimerWarningSeconds"
                type="number"
                :label="$t('editProgram.inputTimerWarningTimeLabel')"
                outlined
                :suffix="$t('editProgram.timerWarningSecondsSuffix')"
                :rules="[val => val > 0 || $t('editProgram.inputIntervalTimerWarningValidator')]"
                lazy-rules
                hide-hint
                hide-bottom-space
                class="col-xs-12 col-md-6"
              />
              <div class="col-xs-12 col-md-6 row items-center q-col-gutter-sm">
                <q-select
                  v-model="targetTimerWarningSound"
                  :options="intervalWarningSounds"
                  emit-value
                  map-options
                  outlined
                  :label="$t('editProgram.inputTimerWarningSoundLabel')"
                  :rules="[val => !!val || $t('editProgram.inputIntervalTimerWarningSoundValidator')]"
                  lazy-rules
                  hide-hint
                  hide-bottom-space
                  class="col"
                >
                  <template #after>
                    <q-btn
                      icon="play_circle"
                      flat
                      round
                      color="primary"
                      @click="onPreviewSound"
                    />
                  </template>
                </q-select>
              </div>
            </template>
          </template>
          <q-select
            v-if="targetTypeHasSubTargets"
            ref="promptLevelTemplate"
            v-model="targetDefaultPromptLevelTemplate"
            :options="promptLevelTemplateOptions"
            :label="$t('editProgram.selectDefaultPromptLevelLabel')"
            outlined
            emit-value
            map-options
            :hint="$t('editProgram.selectDefaultPromptLevelHint')"
            hide-hint
            hide-bottom-space
            lazy-rules
            clearable
            class="col-12"
          >
            <template
              v-if="isCustomizedPromptLevelTemplate(targetDefaultPromptLevelTemplate)"
              #prepend
            >
              <q-icon
                name="new_releases"
                color="primary"
              >
                <q-tooltip>
                  {{ $t('editProgram.selectPromptLevelsCustomizedTooltip') }}
                </q-tooltip>
              </q-icon>
            </template>
            <template
              v-if="targetDefaultPromptLevelTemplate"
              #append
            >
              <q-btn
                round
                flat
                icon="preview"
                @click.stop="onPreviewPromptLevelTemplate(
                  targetDefaultPromptLevelTemplate,
                  isCustomizedPromptLevelTemplate(targetDefaultPromptLevelTemplate),
                )"
              >
                <q-tooltip>
                  {{ $t('common.buttonPreview') }}
                </q-tooltip>
              </q-btn>
            </template>
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
              >
                <q-item-section>
                  <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                  <q-item-label v-html="scope.opt.label" />
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    round
                    flat
                    icon="preview"
                    @click.stop="onPreviewPromptLevelTemplate(scope.opt.value)"
                  >
                    <q-tooltip>
                      {{ $t('common.buttonPreview') }}
                    </q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </template>
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  {{ $t('editProgram.itemNoTemplates') }}
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select
            v-else-if="targetTypeHasPromptLevelTemplate"
            ref="promptLevelTemplate"
            v-model="targetPromptLevelTemplateId"
            :options="promptLevelTemplateOptions"
            :label="$t('editProgram.selectPromptLevelLabel')"
            outlined
            emit-value
            map-options
            :hint="$t('editProgram.selectPromptLevelHint')"
            hide-hint
            hide-bottom-space
            lazy-rules
            clearable
            class="col-12"
          >
            <template
              v-if="isCustomizedPromptLevelTemplate(targetPromptLevelTemplateId)"
              #prepend
            >
              <q-icon
                name="new_releases"
                color="primary"
              >
                <q-tooltip>
                  {{ $t('editProgram.selectPromptLevelsCustomizedTooltip') }}
                </q-tooltip>
              </q-icon>
            </template>
            <template
              v-if="targetPromptLevelTemplateId"
              #append
            >
              <q-btn
                round
                flat
                icon="preview"
                @click.stop="onPreviewPromptLevelTemplate(
                  targetPromptLevelTemplateId,
                  isCustomizedPromptLevelTemplate(targetPromptLevelTemplateId),
                )"
              >
                <q-tooltip>
                  {{ $t('common.buttonPreview') }}
                </q-tooltip>
              </q-btn>
            </template>
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
              >
                <q-item-section>
                  <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                  <q-item-label v-html="scope.opt.label" />
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    round
                    flat
                    icon="preview"
                    @click.stop="onPreviewPromptLevelTemplate(scope.opt.value)"
                  >
                    <q-tooltip>
                      {{ $t('common.buttonPreview') }}
                    </q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </template>
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  {{ $t('editProgram.itemNoTemplates') }}
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select
            v-if="targetTypeHasDefaultObservation"
            v-model="targetDefaultPromptLevel"
            outlined
            :options="subTargetsPromptLevelOptions"
            :label="$t('editProgram.selectDefaultObservationLabel')"
            emit-value
            map-options
            option-value="id"
            option-label="trialOutcome"
            :hint="$t('editProgram.selectDefaultObservationHint.taskAnalysis')"
            hide-hint
            hide-bottom-space
            clearable
            lazy-rules
            class="col-12"
          >
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  {{ $t('editProgram.itemNoObservations') }}
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-input
            v-if="targetTypeHasSubTargetMastering"
            v-model.number="targetSubTargetMasteringPct"
            type="number"
            :label="$t('programs.targetCard.inputTargetMasteringPct')"
            suffix="%"
            min="1"
            max="100"
            :hint="$t('programs.targetCard.inputTargetMasteringPctHint')"
            outlined
            hide-hint
            hide-bottom-space
            :rules="[val => (
              (val===null)
              || ((val >=1) && (val <=100))
              || $t('programs.targetCard.inputTargetMasteringPctValidator')
            )]"
            lazy-rules
            input-class="text-right"
            class="col-12"
          />
          <q-select
            v-if="targetTypeMeasurementMetricsOptions.length > 0"
            v-model="targetMeasurementMetric"
            :options="targetTypeMeasurementMetricsOptions"
            :label="$t('editProgram.textMeasurementMetric')"
            :clearable="!isMeasurementMetricLocked"
            :readonly="isMeasurementMetricLocked"
            :dropdown-icon="isMeasurementMetricLocked ? 'none' : undefined"
            outlined
            emit-value
            map-options
            class="col-12"
            @clear="onClearTargetMeasurementMetric"
          />
          <div
            v-if="isFrequencyTarget"
            class="col-xs-12 col-md-6"
          >
            <q-checkbox
              v-model="targetFillZeroFrequency"
              :label="$t('programs.targetCard.fillZeroFrequencyLabel')"
              :disable="disable || offlineMode"
            />
          </div>
          <workflow-select
            v-if="targetTypeHasWorkflow"
            :model-value="targetWorkflow"
            :workflow-adjustments="targetWorkflowAdjustments"
            :metric="targetMeasurementMetric"
            :data-type="target.dataType"
            :loaded-workflows="workflows"
            :context-label="target.label"
            :disable-adjustment="!userCanAdjustWorkflow"
            :adjustments-tooltip="$t('programs.targetCard.tooltipSelectWorkflowAdjustments')"
            hide-hint
            hide-bottom-space
            @adjust-workflow="onAdjustWorkflow"
            @update:model-value="onChangeWorkflow"
          />
          <workflow-select
            v-if="targetTypeHasSubTargets"
            :model-value="targetDefaultWorkflow"
            :label="$t('editProgram.selectDefaultWorkflowLabel')"
            :workflow-adjustments="targetWorkflowAdjustments"
            :metric="targetMeasurementMetric"
            :loaded-workflows="workflows"
            :context-label="target.label"
            :disable-adjustment="!userCanAdjustWorkflow"
            :adjustments-tooltip="$t('programs.targetCard.tooltipSelectWorkflowAdjustments')"
            hide-hint
            hide-bottom-space
            @adjust-workflow="onAdjustDefaultWorkflow"
            @update:model-value="onChangeDefaultWorkflow"
          />
          <div
            v-if="targetTypeHasInstructions"
            class="full-width"
          >
            <text-editor
              v-model="targetInstructions"
              tables
              indent
            />
          </div>
        </q-card-section>
      </div>
    </q-slide-transition>
    <template v-if="showTargetData && !offlineMode">
      <q-separator />
      <q-card-section
        class="row"
      >
        <div class="row col-12">
          <q-space />
          <q-btn
            round
            flat
            dense
            size="sm"
            icon="close"
            @click="showTargetData = false"
          />
        </div>
        <div class="col-12">
          <target-statistics
            :program="program"
            :program-targets="programTargets"
            :target="target"
            :is-rating-scale="isRatingScalePrompt"
            @update-target-progress-settings="$emit('update-target-progress-settings', $event)"
          />
        </div>
      </q-card-section>
    </template>
  </q-card>
</template>
<script>
import { intervalWarningSounds, measurementMetrics, workflowTypes } from 'boot/globals';
import CustomizePromptLevelTemplate from 'components/Programs/PromptLevels/CustomizePromptLevelTemplate';
import EditPromptLevelTemplate from 'components/Programs/PromptLevels/EditPromptLevelTemplate';
import TargetStatistics from 'components/Programs/TargetStatistics';
import WorkflowSelect from 'components/Programs/Workflows/WorkflowSelect';
import TextEditor from 'components/UI/TextEditor';
import { Timestamp } from 'firebase/firestore';
import { chain, filter, find, findIndex, get, head, isEmpty, map, reject, size, toLower } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import { TARGET_DATA_TYPE_DEFINITIONS } from 'src/constants/targets';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { promptLevels } from 'src/store/promptLevels';
import { workflows } from 'src/store/workflows';
import { copySupportInfoToClipboard } from 'src/utils/AppUtils';
import { getTextColor } from 'src/utils/TargetUtils';
import { defineAsyncComponent } from 'vue';
import { HrCheckbox, HrSegmentedBtn } from 'src/design-system';

export default {
  name: 'TargetCard',

  components: {
    HrCheckbox,
    HrSegmentedBtn,
    TargetList: defineAsyncComponent(() => import('components/Programs/TargetList')),
    TargetStatistics,
    TextEditor,
    WorkflowSelect,
  },

  mixins: [
    Interactions,
    Targets,
    Toolbox,
  ],

  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    program: {
      type: Object,
      required: true,
    },
    targetId: {
      type: String,
      required: true,
    },
    showTargetStatus: {
      type: Boolean,
      default: true,
    },
    forceShowDiscriminativeStimulus: {
      type: Boolean,
      default: false,
    },
    discriminativeStimulusList: {
      type: Array,
      default: () => ([]),
    },
    targetDataOpen: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    noBulkActions: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    targetDuplicateWarning: {
      type: Boolean,
      default: false,
    },
    enterAddsTarget: {
      type: Boolean,
      default: false,
    },
    programWorkflow: {
      type: String,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    packagePromptLevelTemplates: {
      type: Array,
      default: () => [],
    },
    shouldLoadSubTargets: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:modelValue',
    'load-subtargets',
    'enter',
    'change-target-type',
    'subtargets-loaded',
    'select',
    'duplicate',
    'delete',
    'update-target-progress-settings',
    'update-parent-target-status',
  ],

  setup() {
    const { getLocaleDateString, extractDate, formatDate } = useTime();
    return { getLocaleDateString, extractDate, formatDate };
  },

  data() {
    return {
      showTargetData: this.targetDataOpen,
      showSettings: false,

      filteredDiscriminativeStimulusList: [],

      timerTypes: [
        { value: 'up', label: this.$t('editProgram.timerTypes.up') },
        { value: 'down', label: this.$t('editProgram.timerTypes.down') },
        { value: 'session', label: this.$t('editProgram.timerTypes.session') },
      ],
      intervalWarningSounds,
      durationPromptLevelTimingOptions: [
        {
          value: 'start',
          label: this.$t('editProgram.labelDurationPromptLevelTimingOptionStart'),
        },
        {
          value: 'end',
          label: this.$t('editProgram.labelDurationPromptLevelTimingOptionEnd'),
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'hasSupervisorCredentials']),
    ...mapState(workflows, ['workflows', 'workflowStatuses']),
    ...mapState(promptLevels, ['activePromptLevels']),
    ...mapState(network, ['offlineMode']),

    isTargetStatusInvalid() {
      const statuses = this.availableTargetWorkflowStatuses.map((s) => s.value);
      return Boolean(this.target.status) && !statuses.includes(this.target.status);
    }, // isTargetStatusInvalid

    userCanAdjustWorkflow() {
      return this.hasSupervisorCredentials;
    },

    canShowData() {
      return get(this.program, 'client', null) !== null;
    }, // canShowData()

    targetWorkflowStatuses() {
      return get(this.workflowStatuses, this.targetWorkflow || 'default');
    }, // targetWorkflowStatuses()

    availableTargetWorkflowStatuses() {
      return chain(this.targetWorkflowStatuses)
        .values()
        .sortBy('order')
        .reject({ isDeleted: true })
        .value();
    }, // availableTargetWorkflowStatuses()

    targetTypeHasTimerType() {
      return ['rate'].includes(this.target.dataType);
    }, // targetTypeHasTimerType()

    targetTypeHasTimerPreset() {
      return ['rate'].includes(this.target.dataType) && (this.targetTimerType === 'down');
    }, // targetTypeHasTimerPreset()

    programTargets: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    }, // programTargets

    showTargetDiscriminativeStimulus() {
      if (this.isIntervalTarget) {
        return false;
      }
      return this.targetTypeHasDiscriminativeStimulus && this.forceShowDiscriminativeStimulus;
    }, // showTargetDiscriminativeStimulus()

    hasPromptLevelFading() {
      return (
        this.target.status === 'acquisition'
        || this.targetWorkflowStatuses?.[this.target.status]?.type === 'acquisition'
      ) && (['MTL', 'MTL-S'].includes(get(this.targetPromptLevelTemplate, 'fadingStrategy')));
    }, // showPromptLevelFading()

    hasMaintenanceLevel() {
      return (this.target.status === 'mastered')
        && this.programTypeHasMaintenanceInterval
        && this.targetTypeHasMaintenanceInterval
        && this.parentTargetTypeAllowsMaintenanceInterval;
    }, // hasMaintenanceLevel()

    hasParentTarget() {
      return get(this.target, 'parentTarget', null) !== null;
    }, // hasParentTarget()

    isIntervalSubTarget() {
      return this.hasParentTarget && this.isIntervalTarget;
    }, // isIntervalSubTarget()

    hasDependantPromptLevel() {
      return this.program.type === 'esdm';
    }, // hasDependantPromptLevel()

    parentTarget() {
      return chain(this.modelValue)
        .find({ id: get(this.target, 'parentTarget', null) })
        .defaultTo(null)
        .value();
    }, // parentTarget()

    parentDefaultPromptLevelTemplate() {
      if (!this.hasParentTarget) {
        return null;
      }
      return chain(this.availablePromptLevelTemplates)
        .find(({ id: get(this.parentTarget, 'defaultPromptLevelTemplate') }))
        .get('id')
        .value();
    },

    parentDefaultWorkflow() {
      if (!this.hasParentTarget) {
        return null;
      }

      return (this.isParentTargetInvalidWorkflow)
        ? ''
        : this.getActiveWorkflow(this.parentTarget, 'defaultWorkflow');
    }, // parentDefaultWorkflow()

    maintenanceIntervals() {
      return map(
        [1, 7, 14, 30, 91, 183, 365],
        (value) => ({
          value,
          label: this.$t(`programs.workflows.workflowDetails.maintenanceIntervals.I${value}`),
        }),
      );
    }, // maintenanceIntervals()

    target: {
      get() {
        return find(this.modelValue, { id: this.targetId }) || {};
      },
      set(newTarget) {
        const index = findIndex(this.modelValue, { id: newTarget.id });
        if (index !== -1) {
          this.programTargets.splice(index, 1, newTarget);
        }
      },
    }, // target()

    availablePromptLevelTemplates() {
      if (get(this.program, 'package', null) === null) {
        return this.activePromptLevels;
      }
      return this.packagePromptLevelTemplates;
    }, // availablePromptLevelTemplates()

    promptLevelTemplateOptions() {
      return chain(this.availablePromptLevelTemplates)
        .map(
          (promptLevelTemplate) => (
            {
              value: promptLevelTemplate.id,
              label: promptLevelTemplate.templateName,
              isDefault: promptLevelTemplate.isDefault,
            }
          ),
        )
        .sortBy((promptLevelTemplate) => (toLower(promptLevelTemplate.label)))
        .value();
    }, // promptLevelTemplateOptions()

    targetPromptLevelTemplate() {
      return find(this.availablePromptLevelTemplates, { id: this.targetPromptLevelTemplateId });
    }, // targetPromptLevelTemplate()

    availableTargetDataTypes() {
      const types = this.getAvailableTargetDataTypes(
        this.program,
        this.hasParentTarget,
      );
      if (this.parentTargetDataTypeInfo?.value === 'interval') {
        return types.filter((t) => t.value === 'interval');
      }
      if (this.hasParentTarget) {
        return types.filter((t) => t.value !== 'interval');
      }
      return types;
    }, // availableTargetDataTypes()

    hasAvailableTargetDataTypes() {
      return size(this.availableTargetDataTypes) > 0;
    }, // hasAvailableTargetDataTypes()

    targetDataTypeInfo() {
      const currentType = get(this.target, 'dataType');
      const fallbackType = this.parentTargetDataTypeInfo?.value === 'interval' ? 'interval' : undefined;
      return chain(TARGET_DATA_TYPE_DEFINITIONS)
        .find({ value: currentType || fallbackType })
        .defaultTo(head(TARGET_DATA_TYPE_DEFINITIONS))
        .value();
    }, // targetDataTypeInfo()

    parentTargetDataTypeInfo() {
      return chain(TARGET_DATA_TYPE_DEFINITIONS)
        .find({ value: get(this.parentTarget, 'dataType') })
        .defaultTo(null)
        .value();
    }, // parentTargetDataTypeInfo()

    isIntervalTarget() {
      return this.targetDataTypeInfo?.value === 'interval';
    }, // isIntervalTarget()

    showTargetType() {
      return get(this.targetDataTypeInfo, 'showType', false);
    }, // showTargetType()

    targetTypeHasSettings() {
      return get(this.targetDataTypeInfo, 'hasSettings');
    }, // targetTypeHasSettings()

    targetTypeLabel() {
      return get(this.targetDataTypeInfo, 'label');
    }, // targetTypeLabel()

    targetTypeSettingsLabel() {
      if (this.disable) {
        return get(this.targetDataTypeInfo, 'readOnlySettingsLabel', '');
      }
      return get(this.targetDataTypeInfo, 'settingsLabel', '');
    }, // targetTypeSettingsLabel()

    targetTypeHasDiscriminativeStimulus() {
      return get(this.targetDataTypeInfo, 'hasDiscriminativeStimulus', true);
    }, // targetTypeHasDiscriminativeStimulus()

    targetTypeAllowedAsSubTarget() {
      return get(this.targetDataTypeInfo, 'allowAsSubTarget', true);
    }, // targetTypeHasDiscriminativeStimulus()

    targetTypeHasSubTargets() {
      return get(this.targetDataTypeInfo, 'hasSubTargets', false);
    }, // targetTypeHasSubTargets()

    targetTypeMeasurementMetricsOptions() {
      if (this.isIntervalTarget) {
        return measurementMetrics
          .filter((m) => ['percentCorrect', 'frequency'].includes(m.id))
          .map((m) => ({
            value: m.id,
            label: m.label ?? '',
          }));
      }
      const allowedMetrics = this.targetDataTypeInfo?.allowedMetrics || [];

      return allowedMetrics
        .map((metricId) => {
          const measurementMetric = measurementMetrics.find((m) => m.id === metricId);
          return {
            value: metricId,
            label: measurementMetric?.label ?? '',
          };
        });
    }, // targetTypeMeasurementMetricsOptions()

    targetTypeHasWorkflow() {
      if (this.isIntervalTarget && this.hasParentTarget) {
        return false;
      }
      return get(this.targetDataTypeInfo, 'hasWorkflow', false);
    }, // targetTypeHasWorkflow()

    targetTypeHasSubTargetMastering() {
      return get(this.targetDataTypeInfo, 'hasSubTargetMastering', false)
        && (this.targetDefaultWorkflow);
    },

    targetTypeHasDefaultObservation() {
      return get(this.targetDataTypeInfo, 'hasDefaultObservation', false);
    }, // targetTypeHasDefaultObservation()

    targetTypeHasMaintenanceInterval() {
      if (this.hasParentTarget && this.parentTargetDataTypeInfo?.value === 'interval') {
        return true;
      }
      return get(this.targetDataTypeInfo, 'hasMaintenanceInterval', false);
    }, // targetTypeHasMaintenanceInterval()

    programTypeHasMaintenanceInterval() {
      return !['sbt', 'taskAnalysis'].includes(this.program.type);
    },

    targetTypeHasPromptLevelTemplate() {
      return get(this.targetDataTypeInfo, 'hasPromptLevelTemplate', false);
    }, // targetTypeHasPromptLevelTemplate()
    targetTypeHasInstructions() {
      return get(this.targetDataTypeInfo, 'hasInstructions', false);
    }, // targetTypeHasInstructions()

    targetTrialValidator() {
      return [(val) => (val >= 0 || this.$t('editProgram.inputNumberValidatorMessage'))];
    }, // targetNumberValidator()

    isProgramTypeSbt() {
      return ['sbt'].includes(this.program.type);
    }, // isProgramTypeSbt()

    isTargetInvalidWorkflow() {
      return this.target.workflow && !this.getActiveWorkflow(this.target, 'workflow');
    }, // isTargetInvalidWorkflow()

    isTargetInvalidDefaultWorkflow() {
      return get(this.target, 'defaultWorkflow', false) && !this.getActiveWorkflow(this.target, 'defaultWorkflow');
    }, // isTargetInvalidDefaultWorkflow()

    isParentTargetInvalidWorkflow() {
      return get(this.parentTarget, 'defaultWorkflow') && !this.getActiveWorkflow(this.parentTarget, 'defaultWorkflow');
    }, // isParentTargetInvalidWorkflow()

    isTargetOrParentHasInvalidWorkflow() {
      return (this.isTargetInvalidWorkflow || this.isParentTargetInvalidWorkflow);
    }, // isTargetOrParentHasInvalidWorkflow()

    invalidWorkflowTooltip() {
      const selectedWorkflowLabel = this.isTargetInvalidWorkflow
        ? this.$t('editProgram.selectWorkflowLabel')
        : this.$t('editProgram.selectDefaultWorkflowLabel');

      return this.$t('programs.targetCard.targetHasInvalidWorkflow', { selectedWorkflowLabel });
    }, // invalidWorkflowTooltip()

    preventWorkflowInheritance() {
      return get(this.targetDataTypeInfo, 'preventWorkflowInheritance', false);
    },

    parentTargetTypeAllowsMaintenanceInterval() {
      if (!this.hasParentTarget) {
        return true;
      }
      const parentDataType = get(this.parentTarget, 'dataType');
      if (parentDataType === 'interval') {
        return true;
      }
      return get(this.parentTargetDataTypeInfo, 'hasSubTargetMaintenanceInterval', false);
    }, // parentTargetTypeAllowsMaintenanceInterval()

    targetTypeHasSubTargetMaintenanceInterval() {
      return get(this.targetDataTypeInfo, 'hasSubTargetMaintenanceInterval', false);
    }, // targetTypeHasMaintenanceInterval()

    subTargetsPromptLevelOptions() {
      return chain(this.activePromptLevels)
        .find({ id: this.targetDefaultPromptLevelTemplate })
        .get('promptLevels')
        .value();
    }, // subTargetsPromptLevelOptions()

    currentPromptLevelOptions() {
      return get(this.targetPromptLevelTemplate, 'promptLevels');
    }, // currentPromptLevelOptions()

    activePromptLevelOptions() {
      return reject(this.currentPromptLevelOptions, { excludeFromPromptLevelFading: true });
    }, // activePromptLevelOptions()

    displayIntroductionDate() {
      if (
        !this.target.introductionDate
        || typeof this.target.introductionDate.toDate !== 'function'
      ) {
        return 'N/A';
      }
      return this.getLocaleDateString(this.target.introductionDate);
    }, // displayIntroductionDate

    displayMasteryDate() {
      if (
        !this.target.masteryDate
        || typeof this.target.masteryDate.toDate !== 'function'
      ) {
        return 'N/A';
      }
      return this.getLocaleDateString(this.target.masteryDate);
    }, // displayMasteryDate

    displayHoldDate() {
      if (
        !this.target.holdDate
        || typeof this.target.holdDate.toDate !== 'function'
      ) {
        return 'N/A';
      }
      return this.getLocaleDateString(this.target.holdDate);
    }, // displayHoldDate

    displayAbortedDate() {
      if (
        !this.target.abortedDate
        || typeof this.target.abortedDate.toDate !== 'function'
      ) {
        return 'N/A';
      }
      return this.getLocaleDateString(this.target.abortedDate);
    }, // displayAbortedDate

    editIntroductionDate: {
      get() {
        if (!this.target.introductionDate) {
          return '';
        }
        return this.formatDate(this.target.introductionDate.toDate());
      },
      set(d) {
        this.updateTarget({
          introductionDate: Timestamp.fromDate(this.extractDate(d, 'yyyy-MM-dd')),
        });
      },
    }, // editMasteryDate

    editMasteryDate: {
      get() {
        if (!this.target.masteryDate) {
          return '';
        }
        return this.formatDate(this.target.masteryDate.toDate());
      },
      set(d) {
        this.updateTarget({
          masteryDate: Timestamp.fromDate(this.extractDate(d, 'yyyy-MM-dd')),
        });
      },
    }, // editMasteryDate

    editHoldDate: {
      get() {
        if (!this.target.holdDate) {
          return '';
        }
        return this.formatDate(this.target.holdDate.toDate());
      },
      set(d) {
        this.updateTarget({
          holdDate: Timestamp.fromDate(this.extractDate(d, 'yyyy-MM-dd')),
        });
      },
    }, // editHoldDate

    editAbortedDate: {
      get() {
        if (!this.target.abortedDate) {
          return '';
        }
        return this.formatDate(this.target.abortedDate.toDate());
      },
      set(d) {
        this.updateTarget({
          abortedDate: Timestamp.fromDate(this.extractDate(d, 'yyyy-MM-dd')),
        });
      },
    }, // editAbortedDate

    targetSuccessfulTrials: {
      get() {
        return this.target.successfulTrials;
      },
      set(v) {
        this.updateTarget({
          successfulTrials: v,
        });
      },
    }, // targetSuccessfulTrials()

    targetNumberOfTrials: {
      get() {
        return this.target.numberOfTrials;
      },
      set(v) {
        this.updateTarget({
          numberOfTrials: v,
        });
      },
    }, // targetNumberOfTrials()

    discriminativeStimulusListTrigger() {
      return this.discriminativeStimulusList.toString();
    }, // discriminativeStimulusListTrigger

    targetLabel: {
      get() {
        return this.target.label;
      },
      set(value) {
        this.updateTarget({
          label: value,
        });
      },
    }, // targetLabel()

    targetLabelHint() {
      if (!this.enterAddsTarget) {
        return;
      }

      return this.$t('programs.targetCard.inputTargetLabelHint');
    }, // targetLabelHint()

    targetTimerType: {
      get() {
        return get(this.target, 'timerType') || 'up';
      },
      set(timerType) {
        this.updateTarget({
          timerType,
        });
      },
    }, // targetTimerType()

    targetTimerPresetFormatted: {
      get() {
        return this.formatDuration(get(this.target, 'timerPreset')) || null;
      },
      set(timerPresetFormatted) {
        this.updateTarget({ timerPreset: this.stringToDuration(timerPresetFormatted) });
      },
    }, // targetTimerPresetFormatted()

    targetDefaultPromptLevelTemplate: {
      get() {
        return this.activeTargetDefaultPromptLevelTemplate
          || this.activeProgramPromptLevelTemplate;
      },
      set(value) {
        this.updateTarget({ defaultPromptLevelTemplate: value });
      },
    }, // targetDefaultPromptLevelTemplate

    targetPromptLevelTemplateId: {
      get() {
        return this.activeTargetPromptLevelTemplate
          || this.parentDefaultPromptLevelTemplate
          || this.activeProgramPromptLevelTemplate;
      },
      set(value) {
        if (this.parentDefaultPromptLevelTemplate !== null) {
          // Prompt level template inherited from parent
          this.updateTarget({
            promptLevelTemplate: (value === this.parentDefaultPromptLevelTemplate) ? null : value
            ,
          });
          return;
        }
        this.updateTarget({
          promptLevelTemplate: (value === this.activeProgramPromptLevelTemplate) ? null : value
          ,
        });
      },
    }, // targetPromptLevelTemplateId()

    activeProgramPromptLevelTemplate() {
      return chain(this.availablePromptLevelTemplates)
        .find(({ id: get(this.program, 'promptLevelTemplate') }))
        .get('id')
        .value();
    }, // activeProgramPromptLevelTemplate()

    activeTargetPromptLevelTemplate() {
      return chain(this.availablePromptLevelTemplates)
        .find(({ id: get(this.target, 'promptLevelTemplate') }))
        .get('id')
        .value();
    }, // activeTargetPromptLevelTemplate()

    activeTargetDefaultPromptLevelTemplate() {
      return chain(this.availablePromptLevelTemplates)
        .find({ id: get(this.target, 'defaultPromptLevelTemplate') })
        .get('id')
        .value();
    }, // activeTargetDefaultPromptLevelTemplate()

    targetMeasurementMetric: {
      get() {
        if (this.isIntervalTarget) {
          const currentMetric = get(this.target, 'primaryMetric');
          if (['percentCorrect', 'frequency'].includes(currentMetric)) {
            return currentMetric;
          }
          const programMetric = this.program.primaryMetric;
          if (['percentCorrect', 'frequency'].includes(programMetric)) {
            return programMetric;
          }
          return 'percentCorrect';
        }

        if (this.isFrequencyTarget) {
          return 'frequency';
        }

        if (this.targetTypeMeasurementMetricsOptions.length === 0) {
          // Target type has no measurement metrics - inherit from program
          return this.program.primaryMetric || null;
        }

        return this.getTargetMetric(this.target, this.program);
      },
      set(value) {
        const changes = { primaryMetric: value };

        const isIntervalParent = this.isIntervalTarget && !this.hasParentTarget;
        if (isIntervalParent && this.target.defaultWorkflow) {
          if (!this.isWorkflowCompatibleWithMetric(this.target.defaultWorkflow, value)) {
            changes.defaultWorkflow = null;
            changes.defaultWorkflowAdjustments = null;
          }
        }

        this.updateTarget(changes);

        if (this.isIntervalTarget && this.targetTypeHasSubTargets) {
          this.programTargets = this.programTargets.map((item) => {
            if (item.parentTarget === this.target.id) {
              return {
                ...item,
                primaryMetric: value,
                editStatus: item.editStatus === 'create' ? 'create' : 'update',
              };
            }
            return item;
          });
        }
      },
    }, // targetMeasurementMetric()

    targetDefaultWorkflow() {
      let resultWorkflowId = null;

      if (!get(this.target, 'defaultWorkflow', false)) {
        resultWorkflowId = this.programWorkflow;
      } else if (this.isTargetInvalidDefaultWorkflow) {
        return `<span class="text-red-5">${this.$t('programs.targetCard.invalidWorkflowMessage')}</span>`;
      } else {
        resultWorkflowId = this.getActiveWorkflow(this.target, 'defaultWorkflow');
      }

      if (this.isIntervalTarget && resultWorkflowId) {
        const isCompatible = this.isWorkflowCompatibleWithMetric(
          resultWorkflowId,
          this.targetMeasurementMetric,
        );

        if (!isCompatible) {
          return null;
        }
      }

      return resultWorkflowId;
    }, // targetDefaultWorkflow()

    targetSubTargetMasteringPct: {
      get() {
        return get(this.target, 'subTargetMasteringPct') || null;
      },
      set(subTargetMasteringPct) {
        this.updateTarget({ subTargetMasteringPct: subTargetMasteringPct === '' ? null : subTargetMasteringPct });
      },
    }, // targetSubTargetMasteringPct()

    targetWorkflow() {
      if (this.isTargetInvalidWorkflow) {
        return '';
      }

      let activeWorkflowId = this.getActiveWorkflow(this.target, 'workflow') ?? null;

      if (!this.preventWorkflowInheritance) {
        activeWorkflowId = activeWorkflowId ?? this.parentDefaultWorkflow ?? this.programWorkflow;
      }

      if (!activeWorkflowId) {
        return null;
      }

      // Check if the active workflow type is compatible with the target measurement metric
      const activeWorkflow = this.workflows.find((w) => w.id === activeWorkflowId);
      const workflowType = workflowTypes[activeWorkflow?.type || 'accuracyIncrease'];

      const targetMetric = this.getTargetMetric(this.target, this.program);
      if ((workflowType.metric !== targetMetric && activeWorkflow?.type !== 'manual')) {
        // Active workflow is not compatible with the target measurement metric
        return null;
      }

      return activeWorkflowId;
    }, // targetWorkflow()

    targetDefaultPromptLevel: {
      get() {
        return chain(this.availablePromptLevelTemplates)
          .find({ id: this.targetDefaultPromptLevelTemplate })
          .get('promptLevels')
          .find({ id: get(this.target, 'defaultPromptLevel') })
          .get('id')
          .value();
      },
      set(value) {
        this.updateTarget({
          defaultPromptLevel: value,
        });
      },
    }, // targetDefaultPromptLevel()

    targetInstructions: {
      get() {
        return this.target.instructions;
      },
      set(value) {
        this.updateTarget({ instructions: value });
      },
    }, // targetInstructions()

    targetWorkflowAdjustments() {
      const targetWorkflowAdjustments = get(this.target, 'workflowAdjustments', null);
      const targetDefaultWorkflowAdjustments = get(this.target, 'defaultWorkflowAdjustments', null);
      // Skip inherited (parent/program) adjustments when target has its own workflow —
      // those adjustments belong to a different workflow. Mirrors getAdjustedWorkflow in Targets.js.
      const hasOwnWorkflow = !!this.target?.workflow;
      const parentTargetDefaultAdjustments = hasOwnWorkflow
        ? null
        : get(this.parentTarget, 'defaultWorkflowAdjustments', null);
      const programWorkflowAdjustments = hasOwnWorkflow
        ? null
        : get(this.program, 'workflowAdjustments', null);

      return targetWorkflowAdjustments
        ?? targetDefaultWorkflowAdjustments
        ?? parentTargetDefaultAdjustments
        ?? programWorkflowAdjustments;
    }, // targetWorkflowAdjustments()

    targetTypeHasIntervalSettings() {
      return get(this.targetDataTypeInfo, 'hasIntervalSettings', false) && !this.hasParentTarget;
    }, // targetTypeHasIntervalSettings()

    targetIntervalFormatted: {
      get() {
        return this.formatDuration(get(this.target, 'interval', 0)) || null;
      },
      set(intervalFormatted) {
        this.updateTarget({ interval: this.stringToDuration(intervalFormatted) });
      },
    }, // targetIntervalFormatted()

    targetTimerAutoStart: {
      get() {
        return get(this.target, 'timerAutoStart', false);
      },
      set(v) {
        this.updateTarget({ timerAutoStart: v });
      },
    }, // targetTimerAutoStart()

    targetTimerWarningValue: {
      get() {
        return get(this.target, 'timerWarning', false);
      },
      set(v) {
        this.updateTarget({ timerWarning: v });
        if (!v && this.target.timerIntervalPause) {
          this.updateTarget({ timerIntervalPause: false });
        }
      },
    }, // targetTimerWarningValue()

    targetTimerIntervalPauseValue: {
      get() {
        return get(this.target, 'timerIntervalPause', false);
      },
      set(v) {
        this.updateTarget({ timerIntervalPause: v });
      },
    }, // targetTimerIntervalPauseValue()

    targetTimerWarningSeconds: {
      get() {
        return get(this.target, 'timerWarningSeconds', 0);
      },
      set(v) {
        this.updateTarget({ timerWarningSeconds: v });
      },
    }, // targetTimerWarningSeconds()

    targetTimerWarningSound: {
      get() {
        return get(this.target, 'timerWarningSound', '');
      },
      set(v) {
        this.updateTarget({ timerWarningSound: v });
      },
    }, // targetTimerWarningSound()

    canShowTargetSettings() {
      if (this.isIntervalTarget && this.hasParentTarget) {
        return false;
      }
      return this.editable && this.hasAvailableTargetDataTypes;
    }, // canShowTargetSettings()

    isRatingScalePrompt() {
      const outcomeMeasurement = get(this.targetPromptLevelTemplate, 'outcomeMeasurement');
      return outcomeMeasurement === 'rating_scale';
    }, // isRatingScalePrompt()

    isFrequencyTarget() {
      return this.targetDataTypeInfo?.value === 'frequency';
    }, // isFrequencyTarget()

    isMeasurementMetricLocked() {
      return this.isFrequencyTarget;
    }, // isMeasurementMetricLocked()

    targetFillZeroFrequency: {
      get() {
        return get(this.target, 'fillZeroFrequency', false);
      },
      set(v) {
        this.updateTarget({ fillZeroFrequency: v });
      },
    }, // targetFillZeroFrequency

    isDurationTarget() {
      return this.targetDataTypeInfo?.value === 'duration';
    }, //  isDurationTarget()

    showTargetDurationSettings() {
      return this.isDurationTarget && this.program?.type === 'simple';
    }, // showTargetDurationSettings()

    targetDurationPromptLevelTiming: {
      get() {
        return this.target?.durationPromptLevelTiming
          ?? this.program?.durationPromptLevelTiming
          ?? 'start';
      },
      set(v) {
        this.updateTarget({ durationPromptLevelTiming: v });
      },
    }, // targetDurationPromptLevelTiming

    hasMultiplePromptLevels() {
      return size(this.currentPromptLevelOptions) > 1;
    }, // hasMultiplePromptLevels()

    targetFillZeroDuration: {
      get() {
        return this.target?.fillZeroDuration ?? this.program?.fillZeroDuration ?? false;
      },
      set(v) {
        this.updateTarget({ fillZeroDuration: v });
      },
    }, // targetFillZeroDuration
  },

  watch: {
    discriminativeStimulusListTrigger: {
      immediate: true,
      handler() {
        this.filteredDiscriminativeStimulusList = this.discriminativeStimulusList;
      },
    },

    canShowTargetSettings(newValue) {
      if (newValue === false && this.showSettings === true) {
        this.showSettings = false;
      }
    },
  },

  mounted() {
    // Auto-focus elements when added
    if (this.editable && (get(this.target, 'editStatus', '') === 'create')) {
      this.$refs.targetLabel.focus();

      if (this.isIntervalTarget && !this.hasParentTarget) {
        if (!this.target.interval) {
          this.updateTarget({
            interval: 60,
            timerWarningSeconds: 10,
            timerWarning: false,
            timerIntervalPause: false,
            timerAutoStart: true,
            primaryMetric: this.getDefaultIntervalMetric(),
          });
        }
      }

      if (this.parentTargetDataTypeInfo?.value === 'interval') {
        const parentMetric = this.parentTarget?.primaryMetric || this.getDefaultIntervalMetric();

        this.updateTarget({
          dataType: 'interval',
          primaryMetric: this.target.primaryMetric || parentMetric,
        });
      }
    }
  },

  methods: {
    getTextColor,
    ...mapActions(workflows, ['getWorkflowCustomStatuses']),

    showChangeTargetAttention(targetDataType) {
      return this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('editProgram.dialogConfirmTargetTypeChangeAttention'),
        () => {
          this.onPrepareToChangeTargetType(targetDataType);
        },
      );
    },
    onShowSettingsToggle() {
      this.showSettings = !this.showSettings;
      if (this.showSettings && this.targetDataTypeInfo.hasSubTargets) {
        this.$emit('load-subtargets', { targetId: this.target.id });
      }
    },

    onEnterPressed() {
      this.$emit('enter');
    }, // onEnterPressed()

    onPrepareToChangeTargetType(targetDataType) {
      if (targetDataType === get(this.target, 'dataType', 'discreteTrial')) {
        return;
      }

      // change target data type
      if (this.hasParentTarget) {
        this.onChangeTargetType(targetDataType);
        return;
      }

      this.$emit('change-target-type', {
        targetId: this.target.id,
        targetDataType,
      });
    }, // onPrepareToChangeTargetType()

    async onChangeTargetType(targetDataType) {
      const changes = {
        dataType: targetDataType,
      };

      if (this.parentTargetDataTypeInfo?.value === 'interval') {
        changes.dataType = 'interval';
      }

      if (changes.dataType === 'interval' && !this.hasParentTarget) {
        changes.interval = 60;
        changes.timerWarningSeconds = 10;
        changes.timerWarning = false;
        changes.timerIntervalPause = false;
        changes.timerAutoStart = true;
        changes.primaryMetric = this.getDefaultIntervalMetric();
      }

      if (changes.dataType === 'frequency') {
        changes.primaryMetric = 'frequency';

        if (this.target.workflow && !this.isWorkflowCompatibleWithMetric(this.target.workflow, 'frequency')) {
          changes.workflow = null;
          changes.workflowAdjustments = null;
        }
      }

      const currentTargetSubTargets = chain(this.programTargets)
        .filter({ parentTarget: this.target.id })
        .reject({ editStatus: 'deleted' })
        .size()
        .value();

      const newTargetHasSubTargets = chain(TARGET_DATA_TYPE_DEFINITIONS)
        .find({ value: changes.dataType })
        .get('hasSubTargets')
        .value();

      if ((currentTargetSubTargets > 0) && !newTargetHasSubTargets) {
        this.$q
          .dialog({
            title: this.$t('common.dialogTitleConfirm'),
            message: this.$t('programs.targetCard.notifyChangeTargetDataTypeConfirm'),
            cancel: true,
            persistent: true,
          })
          .onOk(async () => {
            // Delete sub-targets or mark them for deleting
            this.programTargets = chain(this.programTargets)
              .reject({ parentTarget: this.target.id, editStatus: 'create' })
              .map((target) => {
                if (target.id === this.target.id) {
                  return {
                    ...target,
                    ...changes,
                    editStatus: this.target.editStatus === 'create' ? 'create' : 'update',
                  };
                }
                if (target.parentTarget !== this.target.id) {
                  return target;
                }
                return { ...target, editStatus: 'delete' };
              })
              .value();

            this.showSettings = chain(TARGET_DATA_TYPE_DEFINITIONS)
              .find({ value: changes.dataType })
              .get('hasSettings')
              .value();
          });

        return;
      }

      this.updateTarget(changes);

      if (currentTargetSubTargets > 0 && newTargetHasSubTargets) {
        this.updateSubTargetsDataType(changes.dataType);
      }

      if (this.targetDataTypeInfo.hasSubTargets) {
        this.$emit('subtargets-loaded', this.target.id);
      }

      this.showSettings = chain(TARGET_DATA_TYPE_DEFINITIONS)
        .find({ value: changes.dataType })
        .get('hasSettings')
        .value();

      if (this.parentTargetDataTypeInfo?.value === 'interval') {
        this.showSettings = false;
      }
    }, // onChangeTargetType()

    updateSubTargetsDataType(newParentDataType) {
      const isNewTypeInterval = newParentDataType === 'interval';

      this.programTargets = this.programTargets.map((target) => {
        if (target.parentTarget === this.target.id) {
          let updatedDataType = null;

          if (isNewTypeInterval && target.dataType !== 'interval') {
            updatedDataType = 'interval';
          } else if (!isNewTypeInterval && target.dataType === 'interval') {
            updatedDataType = 'discreteTrial';
          }

          if (updatedDataType) {
            return {
              ...target,
              dataType: updatedDataType,
              ...(isNewTypeInterval ? {
                primaryMetric: this.target.primaryMetric || this.getDefaultIntervalMetric(),
              } : {}),
              editStatus: target.editStatus === 'create' ? 'create' : 'update',
            };
          }
        }
        return target;
      });
    }, // updateSubTargetsDataType()

    updateTargetWorkflow(newWorkflowId) {
      this.updateTarget({ workflow: newWorkflowId });
    }, // updateTargetWorkflow()

    updateTargetDefaultWorkflow(newWorkflowId) {
      this.updateTarget({
        defaultWorkflow: (newWorkflowId === this.programWorkflow ? null : newWorkflowId),
      });
    }, // updateTargetDefaultWorkflow()

    /**
     * Display confirmation if the user change workflow with custom statuses.
     * Update the target workflow and custom statuses according to the selected option in the confirm dialog.
     *
     * @param {string} newWorkflowId
     * @param {Array<string>} workflowCustomStatuses - list of custom workflow statuses from the previous workflow.
     */
    onChangeStatusConfirmation(newWorkflowId, workflowCustomStatuses) {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('editProgram.dialogConfirmRestoreTargetStatuses'),
        async (response) => {
          if (this.targetTypeHasSubTargets) {
            this.updateTargetDefaultWorkflow(newWorkflowId);
          } else {
            this.updateTargetWorkflow(newWorkflowId);
          }

          if (response === 'default') {
            this.onSetTargetDefaultStatus(workflowCustomStatuses, 'acquisition');
          }
        },
        {
          options: {
            type: 'radio',
            model: 'default',
            items: [
              { label: this.$t('editProgram.dialogSetTargetDefaultStatusOptions[0]'), value: 'default' },
              { label: this.$t('editProgram.dialogSetTargetDefaultStatusOptions[1]'), value: 'custom' },
            ],
          },
        },
      );
    }, // onChangeStatusConfirmation()

    onClearTargetMeasurementMetric() {
      this.updateTarget({ primaryMetric: null });
    }, // onClearTargetMeasurementMetric()

    async onChangeWorkflow(newWorkflowId) {
      const activeWorkflow = this.getActiveWorkflow(this.target, 'workflow')
        ?? this.getActiveWorkflow(this.parentTarget, 'defaultWorkflow')
        ?? this.programWorkflow;
      const workflowCustomStatuses = await this.getWorkflowCustomStatuses(activeWorkflow);
      const isWorkflowCustomStatusesEmpty = isEmpty(workflowCustomStatuses);

      if (this.isProgramTypeSbt) {
        this.notifyWarning(this.$t('editProgram.notifyChangeSBTTargetWorkflow'));
      }

      if (isWorkflowCustomStatusesEmpty || (this.targetTypeHasWorkflow && this.targetTypeHasSubTargets)) {
        this.updateTargetWorkflow(newWorkflowId);
        return;
      }

      this.onChangeStatusConfirmation(newWorkflowId, workflowCustomStatuses);
    }, // onChangeWorkflow()

    async onChangeDefaultWorkflow(newWorkflowId) {
      const activeWorkflow = this.getActiveWorkflow(this.target, 'defaultWorkflow') ?? this.programWorkflow;
      const workflowCustomStatuses = await this.getWorkflowCustomStatuses(activeWorkflow);
      const isWorkflowCustomStatusesEmpty = isEmpty(workflowCustomStatuses);

      if (this.isProgramTypeSbt) {
        this.notifyWarning(this.$t('editProgram.notifyChangeSBTTargetWorkflow'));
      }

      if (isWorkflowCustomStatusesEmpty) {
        this.updateTargetDefaultWorkflow(newWorkflowId);
        return;
      }

      this.onChangeStatusConfirmation(newWorkflowId, workflowCustomStatuses);
    }, // onChangeDefaultWorkflow()

    onAdjustWorkflow(workflowAdjustments) {
      this.updateTarget({ workflowAdjustments });
    },

    onAdjustDefaultWorkflow(defaultWorkflowAdjustments) {
      this.updateTarget({ defaultWorkflowAdjustments });
    },

    onPreviewPromptLevelTemplate(promptLevelTemplateId, customized = false) {
      const promptLevelTemplate = find(this.activePromptLevels, { id: promptLevelTemplateId });

      if (!promptLevelTemplate) {
        return;
      }

      if (customized) {
        return this.openComponentDialog(
          CustomizePromptLevelTemplate,
          {
            promptLevelTemplate,
            program: this.program,
            readonly: true,
          },
          async () => {
            this.$refs.promptLevelTemplate.hidePopup();
          },
        );
      }

      this.openComponentDialog(
        EditPromptLevelTemplate,
        {
          promptLevelTemplate,
          clinic: this.userDetails.clinic,
          readonly: true,
        },
        () => {
          this.$refs.promptLevelTemplate.hidePopup();
        },
      );
    }, // onPreviewPromptLevelTemplate()

    updateTarget(changes = {}) {
      this.target = {
        ...this.target,
        ...changes,
        editStatus: this.target.editStatus === 'create' ? 'create' : 'update',
      };
    },

    /**
     * Change custom target status to acquisition when the user changes the workflow
     *
     * @param {Array<string>} workflowCustomStatuses - array of custom target statuses. If is empty - change
     * status only for the current target, else change statuses for the matched sub-targets
     * @param {string} status - default status 'acquisition'
     */
    onSetTargetDefaultStatus(workflowCustomStatuses, status = 'acquisition') {
      if (!workflowCustomStatuses) {
        this.setTargetStatus(status);
        return;
      }

      this.programTargets = chain(this.programTargets)
        .map((target) => {
          const isParentTarget = target.parentTarget === this.targetId;
          const isTargetStatusValid = workflowCustomStatuses.includes(target.status);
          const isActiveWorkflowAbsent = !this.getActiveWorkflow(target, 'workflow');
          const isSameTarget = target.id === this.target.id;

          const isValidParentTarget = isParentTarget && isTargetStatusValid && isActiveWorkflowAbsent;
          const isSameValidTarget = isSameTarget && isTargetStatusValid;

          if (isValidParentTarget || isSameValidTarget) {
            return {
              ...target,
              status,
              editStatus: 'update',
            };
          }

          return target;
        })
        .value();
    }, // onSetTargetDefaultStatus()

    /**
     * Set target status (public function)
     */
    setTargetStatus(selectedStatus) {
      let targetChanges = { status: selectedStatus };

      if (this.getTargetProperty(selectedStatus, 'resetMasteryDate', false)) {
        targetChanges = { ...targetChanges, masteryDate: null };
      }
      if (this.getTargetProperty(selectedStatus, 'resetIntroductionDate', false)) {
        targetChanges = { ...targetChanges, introductionDate: null };
      }
      if (this.getTargetProperty(selectedStatus, 'resetHoldDate', false)) {
        targetChanges = { ...targetChanges, holdDate: null };
      }
      if (this.getTargetProperty(selectedStatus, 'resetAbortedDate', false)) {
        targetChanges = { ...targetChanges, abortedDate: null };
      }

      if (['mastered', 'closed'].includes(selectedStatus) && !this.target.masteryDate) {
        targetChanges = { ...targetChanges, masteryDate: Timestamp.now() };
      }
      if (['hold'].includes(selectedStatus) && !this.target.holdDate) {
        targetChanges = { ...targetChanges, holdDate: Timestamp.now() };
      }
      if (['aborted'].includes(selectedStatus) && !this.target.abortedDate) {
        targetChanges = { ...targetChanges, abortedDate: Timestamp.now() };
      }

      if ((selectedStatus === 'acquisition')
        && (['MTL', 'MTL-S'].includes(get(this.targetPromptLevelTemplate, 'fadingStrategy')))
        && (!this.target.currentPromptLevel)) {
        const currentPromptLevel = chain(this.activePromptLevelOptions)
          .last()
          .get('id', null)
          .value();
        targetChanges = { ...targetChanges, currentPromptLevel };
      }

      if (this.parentTarget) {
        this.$emit('update-parent-target-status', { targetId: this.parentTarget.id, targetChanges });
      }

      this.updateTarget(targetChanges);
    }, // setTargetStatus()

    onSetSelected(value) {
      this.$emit('select', value);
    },

    onShowHistory() {
      const { hasSubTargets } = this.targetDataTypeInfo;

      if (!hasSubTargets || (hasSubTargets && !this.shouldLoadSubTargets)) {
        return this.onShowHistoryGraph();
      }

      this.$emit('load-subtargets', { targetId: this.target.id, callback: () => this.onShowHistoryGraph() });
    }, // onShowHistory()

    onShowHistoryGraph() {
      this.showTargetData = true;
      this.showSettings = false;
    }, // onShowHistoryGraph()

    onEditSettings() {
      this.showSettings = true;

      if (this.showSettings && this.targetDataTypeInfo.hasSubTargets) {
        this.$emit('load-subtargets', { targetId: this.target.id });
      }
    }, // onEditSettings()

    onDuplicate() {
      this.$emit('duplicate', this.target);
    }, // onDuplicate()

    onDelete() {
      this.$emit('delete', this.target);
    }, // onDelete()

    onCopySupportInfo() {
      copySupportInfoToClipboard({ program: this.program?.id, 'program target': this.target?.id });
    }, // onCopySupportInfo()

    onSelectIntroductionDate() {
      this.$refs.qIntroductionDateProxy.hide();
    }, // onSelectIntroductionDate()

    onSelectMasteryDate() {
      this.$refs.qMasteryDateProxy.hide();
    }, // onSelectMasteryDate()

    onSelectHoldDate() {
      this.$refs.qHoldDateProxy.hide();
    }, // onSelectHoldDate()

    onSelectAbortedDate() {
      this.$refs.qAbortedDateProxy.hide();
    }, // onSelectAbortedDate()

    onChangeTargetStatus(selectedStatus) {
      this.setTargetStatus(selectedStatus);
    }, // onChangeTargetStatus()

    onChangeCurrentPromptLevel(promptLevel) {
      const targetChanges = { currentPromptLevel: promptLevel };
      this.updateTarget(targetChanges);
    }, // onChangeCurrentPromptLevel()

    onChangeMaintenanceInterval(maintenanceInterval) {
      const targetChanges = { maintenanceInterval, maintenanceSessions: 0 };
      this.updateTarget(targetChanges);
    }, // onChangeMaintenanceInterval()

    getTargetProperty(targetStatus, property, defaultValue = '') {
      return get(this.targetWorkflowStatuses, [targetStatus, property], defaultValue);
    }, // getTargetProperty()

    getPromptLevel(promptLevelId) {
      return find(this.currentPromptLevelOptions, { id: promptLevelId });
    }, // getPromptLevel()

    getPromptLevelProperty(promptLevelId, property, defaultValue) {
      return chain(this.currentPromptLevelOptions)
        .find({ id: promptLevelId })
        .get(property)
        .defaultTo(defaultValue)
        .value();
    }, // getPromptLevelProperty()

    getPromptLevelListItemStyle(promptLevel) {
      const activeStyle = get(promptLevel, 'id') === this.target.currentPromptLevel ? '; font-weight:bold;' : '';

      return `${this.getPromptLevelStyle(promptLevel)}${activeStyle}`;
    }, // getPromptLevelListItemStyle

    onFilterDiscriminativeStimulusList(val, update) {
      update(() => {
        const needle = val.toLocaleLowerCase();
        this.filteredDiscriminativeStimulusList = filter(
          this.discriminativeStimulusList,
          (v) => v.toLocaleLowerCase().indexOf(needle) > -1,
        );
      });
    }, // onFilterDiscriminativeStimulusList()

    onSetDiscriminativeStimulusListValue(val) {
      this.updateTarget({
        discriminativeStimulus: val,
      });
    }, // onSetDiscriminativeStimulusListValue()

    getMaintenanceLabel(value) {
      return chain(this.maintenanceIntervals)
        .find({ value: value || 1 })
        .get('label')
        .value();
    }, // getMaintenanceLabel()

    isCustomizedPromptLevelTemplate(promptLevelTemplateId) {
      return !!promptLevelTemplateId && this.program?.customizedPromptLevels?.promptLevelId === promptLevelTemplateId;
    }, // isCustomizedPromptLevelTemplate()

    onPreviewSound() {
      const soundFile = chain(this.intervalWarningSounds)
        .find({ value: (this.targetTimerWarningSound || '') })
        .get('soundFile')
        .value();

      if (soundFile) {
        const audio = new Audio(`audio/${soundFile}`);
        audio.play();
      }
    }, // onPreviewSound()

    isTargetDataTypeActive(dataTypeValue) {
      const currentType = this.target.dataType;
      if (!currentType) {
        const fallback = this.parentTargetDataTypeInfo?.value === 'interval'
          ? 'interval'
          : 'discreteTrial';
        return dataTypeValue === fallback;
      }
      return dataTypeValue === currentType;
    }, // isTargetDataTypeActive()

    getDefaultIntervalMetric() {
      const programMetric = get(this.program, 'primaryMetric');
      return ['percentCorrect', 'frequency'].includes(programMetric)
        ? programMetric
        : 'percentCorrect';
    }, // getDefaultIntervalMetric()

    isWorkflowCompatibleWithMetric(workflowId, metric) {
      const workflow = this.workflows.find((w) => w.id === workflowId);
      if (!workflow || workflow.type === 'manual') {
        return true;
      }
      const workflowMetric = get(workflowTypes, [workflow.type || 'accuracyIncrease', 'metric']);
      return workflowMetric === metric;
    }, // isWorkflowCompatibleWithMetric()
  },
};
</script>
<style lang="scss" scoped>

.prompt-level {
  border-width: 0 5px;
  border-style: solid;
}

.hover-detect-root .show-on-hover-root {
  visibility: hidden;
}

.hover-detect-root:hover .show-on-hover-root {
  visibility: visible;
}

.hover-detect .show-on-hover {
  visibility: hidden;
}

.hover-detect:hover .show-on-hover {
  visibility: visible;
}

.target-drag-handle {
  cursor: move;
}

.target-status-chip {
  padding: 6px 10px;
  min-height: unset;
  :deep(.q-btn-dropdown__arrow) {
    margin-left: 2px;
  }
}

.no-hover-bg {
  &:deep(::before) {
    display: none;
  }
  &:deep(.q-focus-helper) {
    display: none;
  }
  &:hover {
    background: transparent;
  }
}

.target-sd-select {
  margin-top: -10px;
  margin-bottom: -8px;
}

.subtarget-settings-btn {
  margin-top: -8px;
  font-size: 13px;
}

.target-settings-column {
  margin-left: -6px;
  min-width: 0;
}

.truncate-settings-btn {
  max-width: 175px;
  width: 100%;

  :deep(.block) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
    min-width: 0;
    margin-left: 4px;
    margin-right: 4px;
  }

  :deep(.q-icon:not(.q-btn-dropdown__arrow)) {
    font-size: 18px;
    flex: 0 0 auto;
  }

  :deep(.q-btn-dropdown__arrow) {
    flex: 0 0 auto;
  }
}
@media (min-width: 600px) and (max-width: 700px) {
  .responsive-actions-container {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
  }
}

@media only screen and (max-width: $breakpoint-xs-max) {
  .target-duration-toggle :deep(.hr-segmented-btn--legacy .q-btn__content),
  .target-duration-toggle :deep(.hr-segmented-btn__label) {
    font-size: $font-xsmall;
  }
}
</style>
